import { useState, useEffect } from 'react';
import { Box, CircularProgress, Paper, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import { SCUIButton, SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import { ListTab, BulkUploadTab } from './components';
import airdropShipment from '../../assets/icons/svg/pageIcons/airdropShipment.svg';
import { Search } from '@mui/icons-material';
import { SearchDrawer } from './components';
import CutOffQuery from '../../services/query/cutOff/CutOffQuery.services';
import PostCheckoutQuery from '../../services/query/postCheckout/PostCheckoutQuery.service';
import { COP, copCls } from './cutOff.classes';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps): React.JSX.Element | null => {
    if (value !== index) return null;
    return (
        <Paper
            className={COP.tabPanel}
            component="div"
            role="tabpanel"
            id={`lcw-cargo-fee-tabpanel-${index}`}
            aria-labelledby={`lcw-cargo-fee-tab-${index}`}
            sx={{
                p: '24px',
                display: 'flex',
                flex: 1,
                backgroundColor: 'grey.50',
            }}
            {...other}
        >
            {children}
        </Paper>
    );
};

interface TabConfig {
    label: string;
    Component: React.ComponentType<any>;
}

export const CutOff = (): React.JSX.Element => {
    const [tabValue, setTabValue] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSearched, setIsSearched] = useState<boolean>(false);
    const [searchDrawerOpen, setSearchDrawerOpen] = useState<boolean>(false);
    const [gridData, setGridData] = useState<any[]>([]);
    const [lastFilterParams, setLastFilterParams] = useState<any>(null);
    const [cargoCompanyOptions, setCargoCompanyOptions] = useState<{ id: number; name: string }[]>([]);

    const { t } = useTranslation();
    const ALL_OPTION_ID = -1;

    // Fetch cargo companies once on page load
    useEffect(() => {
        PostCheckoutQuery.GetShippingCompanies()
            .then((res: any) => {
                if (res?.shippingCompanies && Array.isArray(res.shippingCompanies)) {
                    const options = res.shippingCompanies
                        .filter((company: any) => company?.shippingCompanyId != null && company?.name != null)
                        .map((company: any) => ({
                            id: company.shippingCompanyId,
                            name: company.name ?? '',
                        }));
                    setCargoCompanyOptions(options);
                }
            })
            .catch((err: any) => {
                console.error('Error fetching cargo companies:', err);
            });
    }, []);

    const handleChange = (_: any, newValue: number): void => {
        setTabValue(newValue);
    };

    const tabsConfig: TabConfig[] = [
        {
            label: t('cutOff.tabs.list'),
            Component: ListTab as React.ComponentType<any>,
        },
        {
            label: t('cutOff.tabs.bulkUpload'),
            Component: BulkUploadTab as React.ComponentType<any>,
        },
    ];

    // Transform selected arrays: if "All" is selected or empty, send [-1]
    const transformToApiArray = (selectedArray: any[]): number[] => {
        if (!selectedArray?.length || selectedArray.includes(ALL_OPTION_ID)) {
            return [ALL_OPTION_ID];
        }
        return selectedArray;
    };

    /**
     * Build API request payload from filter params
     */
    const buildRequestPayload = (filterParams?: any) => ({
        shippingCompanyIds: transformToApiArray(filterParams?.selectedCargoCompany ?? []),
        departureLocationIds: transformToApiArray(filterParams?.selectedDepartureLocation ?? []),
        arrivalLocationIds: transformToApiArray(filterParams?.selectedArrivalLocation ?? []),
        distanceRangeIds: transformToApiArray(filterParams?.selectedDistance ?? []),
        deliveryTypeIds: transformToApiArray(filterParams?.selectedDeliveryType ?? []),
        isActive: filterParams?.isActive ?? null,
    });

    /**
     * Transform filter params and call the API
     * If "All" is selected (contains ALL_OPTION_ID) for a field, send [-1]
     * If both Active and Passive are selected for isActive, send null
     */
    const searchList = (filterParams?: any) => {
        setIsLoading(true);
        setSearchDrawerOpen(false);

        // Store filter params for later refresh
        setLastFilterParams(filterParams);

        const requestPayload = buildRequestPayload(filterParams);

        CutOffQuery.GetShippingCompanyCutOffList(requestPayload)
            .then((res: any) => {
                const data = res?.items ?? [];
                setGridData(Array.isArray(data) ? data : []);
                setIsSearched(true);
            })
            .catch((err: any) => {
                console.error('Error fetching cut-off list:', err);
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('cutOff.filterError'),
                    confirmButtonText: t('general.ok'),
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    /**
     * Refresh grid data using the last filter params
     */
    const refreshGridData = () => {
        if (!lastFilterParams) return;

        setIsLoading(true);
        const requestPayload = buildRequestPayload(lastFilterParams);

        CutOffQuery.GetShippingCompanyCutOffList(requestPayload)
            .then((res: any) => {
                const data = res?.items ?? [];
                setGridData(Array.isArray(data) ? data : []);
            })
            .catch((err: any) => {
                console.error('Error refreshing cut-off list:', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <SCUIContent>
            <Paper
                className={COP.page}
                component="div"
                sx={{
                    p: '24px',
                    paddingTop: '36px',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <SCUIBreadCrumb
                    title={t('cutOff.title')}
                    componentLink={PageUrl.cutOff.url}
                    onClick={() => {
                        setTabValue(0);
                        setIsSearched(false);
                    }}
                />
                <SCUILoadingLinear display={isLoading} />
                <SearchDrawer
                    isDrawerOpen={searchDrawerOpen}
                    handleDrawerClose={() => setSearchDrawerOpen(false)}
                    handleSearch={(filterParams: any) => searchList(filterParams)}
                    cargoCompanyOptions={cargoCompanyOptions}
                />
                {isSearched ? (
                    <Box className={COP.tabsSection}>
                        <Tabs
                            className={COP.tabs}
                            sx={{ marginTop: '12px' }}
                            value={tabValue}
                            onChange={handleChange}
                            aria-label="Cut Off Tabs"
                        >
                            {tabsConfig.map((tab, index) => (
                                <Tab
                                    key={index}
                                    className={copCls(COP.tabSlot, `cut-off-page__tab-slot--${index}`)}
                                    sx={{ fontSize: '14px' }}
                                    label={tab.label}
                                    id={`cut-off-tab-${index}`}
                                    aria-controls={`cut-off-tabpanel-${index}`}
                                />
                            ))}
                        </Tabs>
                        {tabsConfig.map((tab, index) => (
                            <TabPanel key={index} value={tabValue} index={index}>
                                <tab.Component
                                    setIsLoading={setIsLoading}
                                    isLoading={isLoading}
                                    cargoCompanyOptions={cargoCompanyOptions}
                                    gridData={gridData}
                                    setGridData={setGridData}
                                    refreshGridData={refreshGridData}
                                />
                            </TabPanel>
                        ))}
                    </Box>
                ) : (
                    <Box
                        className={COP.emptyState}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SCUINoRecordComponent img={airdropShipment} text={t('cutOff.search')} />
                        <SCUIButton
                            className={COP.inputButtonOpenSearch}
                            text={t('general.search')}
                            type="primary"
                            sizeType="fixed"
                            size={'m'}
                            showStartIcon
                            style={{ width: '120px', marginTop: '16px' }}
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={20} style={{ color: 'white' }} />
                                ) : (
                                    <Search style={{ color: 'white' }} />
                                )
                            }
                            onClick={() => setSearchDrawerOpen(true)}
                        />
                    </Box>
                )}
            </Paper>
        </SCUIContent>
    );
};
